import { useCallback, useState, useEffect, useMemo, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useApplePayCapability,
  ApplePayCapability,
} from "@/lib/hooks/useApplePayCapability";
import { useFormRouter } from "@/lib/hooks/useFormRouter";
import { PaymentFormConfig } from "@evergiving/common/dist/es2015/form";

import { RootState } from "@/lib/redux/types";
import { PaymentFrequencyOptions } from "@/lib/components/PaymentFrequencyOptions";
import { AmountOptions } from "@/lib/components/AmountOptions";
import { ProceedToPaymentButton } from "@/lib/components/ProceedToPaymentButton";
import { CountrySwitcher } from "@/lib/components/CountrySwitcher";
import { createIndexPageErrorsSelector } from "@/lib/misc/validations";
import { setFormErrors } from "@/lib/redux/actions";
import { LogoContainer } from "@/lib/components/LogoContainer";
import { useNavigationWarning } from "@/lib/hooks/useNavigationWarning";
import identity from "lodash/identity";
import { PageContainer } from "@/lib/components/PageContainer";

export function DonationIndexPage({
  formConfig,
}: {
  formConfig: PaymentFormConfig;
}): ReactElement {
  const applePayCapability = useApplePayCapability(
    formConfig.payment.applePay?.merchantId,
    {
      checkForActiveCard: true,
    }
  );
  const [paymentRequested, setPaymentRequested] = useState<boolean>(false);

  const indexPageErrorsSelector = useMemo(
    () => createIndexPageErrorsSelector(formConfig),
    [formConfig]
  );

  const errors = useSelector(indexPageErrorsSelector);

  const dispatch = useDispatch();

  const anyErrors = Object.values(errors).some(identity);

  const handlePaymentRequested = useCallback(() => {
    if (anyErrors) {
      dispatch(setFormErrors(errors));
    } else {
      setPaymentRequested(true);
    }
  }, [anyErrors, dispatch, errors]);

  const formRouter = useFormRouter();

  useEffect(() => {
    if (paymentRequested) {
      if (
        applePayCapability === ApplePayCapability.CanMakePaymentsWithActiveCard
      ) {
        formRouter.goToPage("wallet");
      } else if (applePayCapability !== ApplePayCapability.CanMakePayments) {
        formRouter.goToPage("details");
      }
    }
  }, [paymentRequested, applePayCapability, formRouter]);

  useEffect(() => {
    if (!anyErrors) {
      if (
        applePayCapability === ApplePayCapability.CanMakePaymentsWithActiveCard
      ) {
        formRouter.prefetchPage("wallet");
      } else if (applePayCapability !== ApplePayCapability.CanMakePayments) {
        formRouter.prefetchPage("details");
      }
    }
  }, [anyErrors, applePayCapability, formRouter]);

  const paymentFrequency = useSelector(
    (state: RootState) => state.paymentFrequency
  );

  const paymentFrequencyConfig =
    paymentFrequency && formConfig.payment.frequencies[paymentFrequency];

  useNavigationWarning(formConfig);

  return (
    <div id="container" className="paymentFormStart" lang={formConfig.locale}>
      <header className="large">
        <LogoContainer formConfig={formConfig} />
      </header>
      <CountrySwitcher otherLocales={formConfig.otherLocales} />
      <PageContainer formConfig={formConfig}>
        <main>
          <form
            id="amount-frequency"
            aria-label="Donation amount and frequency"
          >
            <PaymentFrequencyOptions
              frequencies={formConfig.payment.frequencies}
            />
            {paymentFrequencyConfig !== undefined && (
              <>
                <AmountOptions
                  locale={formConfig.locale}
                  donationAmountText={formConfig.payment.donationAmountText}
                  paymentConfig={paymentFrequencyConfig}
                />
                <ProceedToPaymentButton
                  onPaymentRequested={handlePaymentRequested}
                  labelText={formConfig.payment.proceedToPaymentButtonText}
                />
              </>
            )}
          </form>
        </main>
      </PageContainer>
    </div>
  );
}
